import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useDroits } from '../auth';
import { useAction, useApi } from '../hooks';
import { Champ, ChampsMachine, Chargement, ChoixLogiciels, MessageErreur, Modal, Statut, StatutLicence, formatDate } from '../components/ui';

/** Page /postes/:id */
export default function PosteDetail() {
  const { id } = useParams();
  return <ContenuPoste id={id} />;
}

/**
 * Fiche poste ouverte en fenêtre par-dessus une liste.
 * `depuisClient` : ouverte depuis la fiche du cabinet, qu'on retrouve en la fermant.
 */
export function FichePoste({ id, onFermer, depuisClient = false }) {
  return <ContenuPoste id={id} onFermer={onFermer} depuisClient={depuisClient} />;
}

function ContenuPoste({ id, onFermer, depuisClient }) {
  const enFenetre = Boolean(onFermer);
  const { support } = useDroits();
  const { donnees: p, erreur, recharger } = useApi(`/postes/${id}`);
  const action = useAction();
  const [modal, setModal] = useState(null);
  const [logicielCle, setLogicielCle] = useState(null); // logiciel dont on change la licence
  const [confirmation, setConfirmation] = useState(null);
  const navigate = useNavigate();

  if (erreur || !p) {
    const etat = erreur ? <MessageErreur erreur={erreur} /> : <Chargement />;
    return enFenetre ? (
      <Modal titre="Fiche poste" onFermer={onFermer} classe="modal-fiche">
        {etat}
      </Modal>
    ) : (
      etat
    );
  }

  // Exécute une action puis recharge la fiche
  const executer = (fn) =>
    action.executer(async () => {
      await fn();
      recharger();
    });

  // Une mise à jour est à envoyer si le poste n'a pas la dernière version et qu'aucune n'est déjà en attente
  const aEnvoyer = (d) => d.derniereVersion && d.derniereVersion !== d.versionInstallee && !d.miseAJour;

  // Envoie la mise à jour vers la dernière version (le logiciel doit être autorisé)
  const envoyer = (d) =>
    executer(() => api('/mises-a-jour', { methode: 'POST', corps: { workstationId: p.id, softwareId: d.logiciel.id } }));

  // Autorise ou interdit les mises à jour d'un logiciel du poste (interdire révoque la mise à jour en attente)
  const changerAutorisation = (d, autorise) =>
    executer(() => api(`/postes/${id}/logiciels/${d.logiciel.id}`, { methode: 'PUT', corps: { updateAllowed: autorise } }));
  const interdire = (d) => {
    const message = d.miseAJour
      ? `Interdire les mises à jour de ${d.logiciel.name} sur ce poste ? La mise à jour en attente sera révoquée.`
      : `Interdire les mises à jour de ${d.logiciel.name} sur ce poste ?`;
    if (window.confirm(message)) changerAutorisation(d, false);
  };

  const supprimer = () => {
    if (!window.confirm(`Supprimer le poste ${p.name} ? Sa licence redeviendra disponible.`)) return;
    action.executer(async () => {
      await api(`/postes/${id}`, { methode: 'DELETE' });
      if (enFenetre) onFermer();
      else navigate(`/clients/${p.client.id}`);
    });
  };

  // Ferme la licence d'un logiciel du poste (il ne démarre plus à son prochain lancement) ou la rouvre
  const basculerLogiciel = (d) => {
    const nom = d.logiciel.name;
    const message = d.ferme
      ? `Rouvrir ${nom} sur ce poste ? Il pourra de nouveau démarrer (une clé d'activation peut être nécessaire).`
      : `Fermer ${nom} sur ce poste ? Au prochain démarrage du poste (avec Internet), sa licence sera effacée.`;
    if (!window.confirm(message)) return;
    executer(() => api(`/postes/${id}/logiciels/${d.logiciel.id}`, { methode: 'PUT', corps: { closed: !d.ferme } }));
  };

  const titre = (
    <>
      {p.name}
      {enFenetre && !depuisClient && <span className="muted">· {p.client.name}</span>} <Statut valeur={p.status} />
    </>
  );
  const boutons = support && (
    <div className="boutons">
      <button className="btn btn-secondaire" onClick={() => setModal('modifier')}>
        Modifier
      </button>
      <button className="btn btn-secondaire" onClick={() => setModal('client')}>
        Changer de client
      </button>
      <button className="btn btn-danger" onClick={supprimer}>
        Supprimer
      </button>
    </div>
  );

  const contenu = (
    <>
      <MessageErreur erreur={action.erreur} />

      <div className="grille-2">
        <section className="carte">
          <h2>Licence</h2>
          <dl className="infos">
            <dt>Clé</dt>
            <dd>{p.license.licenseKey}</dd>
            <dt>Statut</dt>
            <dd>
              <StatutLicence active={p.licenceActive} actives={p.licencesActives} total={p.nombreLogiciels} />
            </dd>
            <dt>Ajouté le</dt>
            <dd>{formatDate(p.license.activatedAt)}</dd>
            <dt>Désactivé le</dt>
            <dd>{formatDate(p.license.expiresAt)}</dd>
            <dt>Envoie statistiques</dt>
            <dd>{p.sendStatistics ? 'Oui' : 'Non'}</dd>
          </dl>
        </section>
        <section className="carte">
          <h2>Machine</h2>
          <dl className="infos">
            <dt>Nom machine</dt>
            <dd>{p.hostname || '—'}</dd>
            <dt>Système</dt>
            <dd>{p.operatingSystem || '—'}</dd>
            <dt>Adresse IP</dt>
            <dd>{p.ipAddress || '—'}</dd>
            <dt>Dernière connexion</dt>
            <dd>{formatDate(p.lastConnectionAt, true)}</dd>
          </dl>
        </section>
      </div>

      <section className="carte">
        <h2>Logiciels</h2>
        <table className="tableau-logiciels">
          <thead>
            <tr>
              <th>Logiciel</th>
              <th>Version installée</th>
              <th>Dernière version</th>
              <th>Activé le</th>
              <th>Expire le</th>
              <th className="centre">Licence</th>
              <th>État de la mise à jour</th>
              <th>Autorisation mise à jour</th>
            </tr>
          </thead>
          <tbody>
            {p.detail
              .filter((d) => d.installe)
              .map((d) => {
                const enRetard = d.derniereVersion && d.derniereVersion !== d.versionInstallee;
                return (
                  <tr key={d.logiciel.id}>
                    <td>{d.logiciel.name}</td>
                    <td>{d.versionInstallee || <span className="muted">inconnue</span>}</td>
                    <td>{d.derniereVersion || '—'}</td>
                    <td>{formatDate(d.activeLe)}</td>
                    <td>{formatDate(d.expireLe)}</td>
                    <td>
                      <div className="cellule-licence">
                        <StatutLicence active={d.licenceActive} />
                        {d.cleEnAttente && (
                          <span className="muted" title="Clé d'activation en attente du prochain démarrage du poste">
                            Clé en attente
                          </span>
                        )}
                        {support && !d.ferme && (
                          <button className="btn btn-secondaire btn-petit" onClick={() => setLogicielCle(d.logiciel)}>
                            Changer
                          </button>
                        )}
                        {support && (
                          <button className="btn btn-secondaire btn-petit" disabled={action.enCours} onClick={() => basculerLogiciel(d)}>
                            {d.ferme ? 'Rouvrir' : 'Fermer'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      {d.miseAJour ? (
                        <>
                          <Statut valeur={d.miseAJour.status} /> → {d.miseAJour.versionCible}
                        </>
                      ) : enRetard ? (
                        <Statut valeur="NON_A_JOUR" />
                      ) : (
                        <Statut valeur="A_JOUR" />
                      )}
                    </td>
                    <td>
                      <div className="cellule-licence">
                        {d.updateAllowed ? (
                          <span className="badge badge-vert">Autorisée</span>
                        ) : (
                          <span className="badge badge-gris">Interdite</span>
                        )}
                        {support &&
                          (d.updateAllowed ? (
                            <button className="btn btn-secondaire btn-petit" disabled={action.enCours} onClick={() => interdire(d)}>
                              Interdire
                            </button>
                          ) : (
                            <button className="btn btn-secondaire btn-petit" disabled={action.enCours} onClick={() => setConfirmation(d)}>
                              Autoriser
                            </button>
                          ))}
                        {support && d.miseAJour && (
                          // Mise à jour en attente : la révoquer revient à l'annuler (page Mises à jour)
                          <button
                            className="btn btn-lien btn-danger"
                            disabled={action.enCours}
                            onClick={() => executer(() => api(`/mises-a-jour/${d.miseAJour.id}`, { methode: 'DELETE' }))}
                          >
                            Révoquer la mise à jour
                          </button>
                        )}
                        {support && d.updateAllowed && aEnvoyer(d) && (
                          <button className="btn btn-principal btn-petit" disabled={action.enCours} onClick={() => envoyer(d)}>
                            Lancer la mise à jour
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            {!p.detail.some((d) => d.installe) && (
              <tr>
                <td colSpan={8} className="muted">
                  Aucun logiciel sur ce poste.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <p>
          <Link to={`/mises-a-jour?workstationId=${p.id}`}>Voir l'historique des mises à jour de ce poste ›</Link>
        </p>
      </section>

      {confirmation && (
        <Modal titre="Autoriser la mise à jour" onFermer={() => setConfirmation(null)}>
          <dl className="infos">
            <dt>Cabinet</dt>
            <dd>{p.client.name}</dd>
            <dt>Poste</dt>
            <dd>{p.name}</dd>
            <dt>Logiciel</dt>
            <dd>{confirmation.logiciel.name}</dd>
          </dl>
          <p>
            Êtes-vous sûr de vouloir autoriser la mise à jour de <strong>{confirmation.logiciel.name}</strong> sur ce poste ?
            Le bouton « Lancer la mise à jour » sera ensuite disponible.
          </p>
          <div className="actions-modal">
            <button type="button" className="btn btn-secondaire" onClick={() => setConfirmation(null)}>
              Annuler
            </button>
            <button
              className="btn btn-principal"
              disabled={action.enCours}
              onClick={() => {
                changerAutorisation(confirmation, true);
                setConfirmation(null);
              }}
            >
              Oui, autoriser
            </button>
          </div>
        </Modal>
      )}
      {modal === 'modifier' && (
        <ModificationPoste
          poste={p}
          onFermer={() => setModal(null)}
          onModifie={() => {
            setModal(null);
            recharger();
          }}
        />
      )}
      {logicielCle && (
        <ChangementLicence
          poste={p}
          logiciel={logicielCle}
          onFermer={() => setLogicielCle(null)}
          onModifie={() => {
            setLogicielCle(null);
            recharger();
          }}
        />
      )}
      {modal === 'client' && (
        <ChangementClient
          poste={p}
          onFermer={() => setModal(null)}
          onModifie={() => {
            setModal(null);
            recharger();
          }}
        />
      )}
    </>
  );

  if (enFenetre) {
    return (
      <Modal titre={titre} libelle={`Fiche poste ${p.name}`} actions={boutons} onFermer={onFermer} classe="modal-fiche">
        {contenu}
      </Modal>
    );
  }

  return (
    <>
      <p>
        <Link to={`/clients/${p.client.id}`}>‹ {p.client.name}</Link>
      </p>
      <div className="titre-page">
        <h1>{titre}</h1>
        {boutons}
      </div>
      {contenu}
    </>
  );
}

/** Date de l'API → valeur d'un champ <input type="date"> (aaaa-mm-jj), vide si absente */
const dateSaisie = (date) => (date ? String(date).slice(0, 10) : '');

function ModificationPoste({ poste, onFermer, onModifie }) {
  const { donnees: logiciels } = useApi('/logiciels');
  const { executer, enCours, erreur } = useAction();
  const [valeurs, setValeurs] = useState({
    name: poste.name,
    hostname: poste.hostname ?? '',
    operatingSystem: poste.operatingSystem ?? '',
    sendStatistics: poste.sendStatistics,
    softwareIds: poste.logiciels.map((l) => l.id),
    licenseKey: poste.license.licenseKey,
    activatedAt: dateSaisie(poste.license.activatedAt),
    expiresAt: dateSaisie(poste.license.expiresAt),
  });

  const soumettre = (e) => {
    e.preventDefault();
    executer(async () => {
      await api(`/postes/${poste.id}`, {
        methode: 'PUT',
        corps: { ...valeurs, activatedAt: valeurs.activatedAt || null, expiresAt: valeurs.expiresAt || null },
      });
      onModifie();
    });
  };

  return (
    <Modal titre={`Modifier ${poste.name}`} onFermer={onFermer}>
      <form onSubmit={soumettre}>
        <MessageErreur erreur={erreur} />
        <Champ libelle="Nom du poste *">
          <input value={valeurs.name} onChange={(e) => setValeurs({ ...valeurs, name: e.target.value })} required />
        </Champ>
        <ChampsMachine valeur={valeurs} onChange={(champs) => setValeurs({ ...valeurs, ...champs })} />
        <Champ libelle="Clé *" aide="Identifiant du poste : il doit correspondre à celui affiché par le logiciel sur ce poste">
          <input value={valeurs.licenseKey} onChange={(e) => setValeurs({ ...valeurs, licenseKey: e.target.value })} required />
        </Champ>
        <Champ libelle="Ajouté le">
          <input type="date" value={valeurs.activatedAt} onChange={(e) => setValeurs({ ...valeurs, activatedAt: e.target.value })} />
        </Champ>
        <Champ libelle="Désactivé le">
          <input type="date" value={valeurs.expiresAt} onChange={(e) => setValeurs({ ...valeurs, expiresAt: e.target.value })} />
        </Champ>
        <label className="case">
          <input
            type="checkbox"
            checked={valeurs.sendStatistics}
            onChange={(e) => setValeurs({ ...valeurs, sendStatistics: e.target.checked })}
          />
          Envoie statistiques
        </label>
        <Champ libelle="Logiciels installés" aide="Retirer un logiciel annule ses mises à jour en attente">
          {logiciels && (
            <ChoixLogiciels
              logiciels={logiciels}
              valeur={valeurs.softwareIds}
              onChange={(softwareIds) => setValeurs({ ...valeurs, softwareIds })}
            />
          )}
        </Champ>
        <div className="actions-modal">
          <button type="button" className="btn btn-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button className="btn btn-principal" disabled={enCours}>
            Enregistrer
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ChangementLicence({ poste, logiciel, onFermer, onModifie }) {
  const { admin } = useDroits();
  const { executer, enCours, erreur } = useAction();
  // Générer la clé sur le serveur (administrateurs) ou coller une clé de Mozaiss Code Generator
  const [mode, setMode] = useState(admin ? 'generer' : 'coller');
  const [expiresAt, setExpiresAt] = useState('');
  const [permanente, setPermanente] = useState(false);
  const [activationKey, setActivationKey] = useState('');

  const soumettre = (e) => {
    e.preventDefault();
    executer(async () => {
      if (mode === 'generer') {
        await api(`/postes/${poste.id}/logiciels/${logiciel.id}/cle`, {
          methode: 'POST',
          corps: { expiresAt: permanente ? null : expiresAt },
        });
      } else {
        await api(`/postes/${poste.id}/licence`, { methode: 'PUT', corps: { activationKey, softwareId: logiciel.id } });
      }
      onModifie();
    });
  };

  return (
    <Modal titre={`Changer de licence — ${logiciel.name}`} onFermer={onFermer}>
      <form onSubmit={soumettre}>
        <MessageErreur erreur={erreur} />
        {admin && (
          <div className="boutons">
            <label className="case">
              <input type="radio" checked={mode === 'generer'} onChange={() => setMode('generer')} />
              Générer la clé
            </label>
            <label className="case">
              <input type="radio" checked={mode === 'coller'} onChange={() => setMode('coller')} />
              Coller une clé
            </label>
          </div>
        )}
        {mode === 'generer' ? (
          <>
            <p className="muted">
              La clé {logiciel.name} de l'identifiant <strong>{poste.license.licenseKey}</strong> est générée par le site et
              installée sur le poste à son prochain démarrage.
            </p>
            <Champ libelle="Expire le">
              <input
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                disabled={permanente}
                required={!permanente}
              />
            </Champ>
            <label className="case">
              <input type="checkbox" checked={permanente} onChange={(e) => setPermanente(e.target.checked)} />
              Licence permanente (sans date d'expiration)
            </label>
          </>
        ) : (
          <>
            <p className="muted">
              Collez la clé {logiciel.name} générée par Mozaiss Code Generator pour l'identifiant{' '}
              <strong>{poste.license.licenseKey}</strong>. Elle sera installée sur le poste à son prochain démarrage ;
              l'expiration est celle de la clé.
            </p>
            <Champ libelle="Clé d'activation (M27-…) *">
              <textarea rows={4} value={activationKey} onChange={(e) => setActivationKey(e.target.value)} required />
            </Champ>
          </>
        )}
        <div className="actions-modal">
          <button type="button" className="btn btn-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button className="btn btn-principal" disabled={enCours}>
            {mode === 'generer' ? 'Générer et envoyer' : 'Changer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ChangementClient({ poste, onFermer, onModifie }) {
  const { donnees: clients } = useApi('/clients', { actif: 'oui', parPage: 100 });
  const { executer, enCours, erreur } = useAction();
  const [clientId, setClientId] = useState('');

  const soumettre = (e) => {
    e.preventDefault();
    executer(async () => {
      await api(`/postes/${poste.id}/client`, { methode: 'PUT', corps: { clientId: Number(clientId) } });
      onModifie();
    });
  };

  return (
    <Modal titre="Changer de client" onFermer={onFermer}>
      <form onSubmit={soumettre}>
        <MessageErreur erreur={erreur} />
        <p className="muted">
          Client actuel : <strong>{poste.client.name}</strong>. Le poste garde sa licence, ses logiciels et ses mises à jour.
        </p>
        <Champ libelle="Nouveau client *">
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} required>
            <option value="">Choisir un client…</option>
            {clients?.donnees
              .filter((c) => c.id !== poste.client.id)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code})
                </option>
              ))}
          </select>
        </Champ>
        <div className="actions-modal">
          <button type="button" className="btn btn-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button className="btn btn-principal" disabled={enCours || !clientId}>
            Changer
          </button>
        </div>
      </form>
    </Modal>
  );
}
